import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { ticketsService } from '@/features/booking/services/ticketsService';
import { transferService } from '@/features/booking/services/transferService';
import type { BookingResponseWithQRCode } from '@/features/booking/types/bookingTypes';
import { BookingStatus } from '@/features/booking/types/bookingTypes';

import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { getTicketCategoryStyle } from './TicketCardStyles';
import { TierRibbon } from './TierRibbon';
import { StatusOverlay, getStatusMessage, isAbandonedStatus } from './StatusOverlay';
import { useStatusTest, useClassTest } from './BookingStatusSwitcher';

interface TicketModalProps {
  bookingId: string;
  onClose: () => void;
}

const DetailRow: React.FC<{ label: string; value: string | number; index: number; truncate?: boolean; accentColor?: string; secondaryColor?: string; borderColor?: string }> = ({ label, value, index, truncate = false, accentColor, secondaryColor, borderColor }) => (
  <motion.div
    className="flex justify-between items-center py-3 border-b"
    style={{ borderColor: borderColor || 'rgba(75, 85, 99, 0.5)' }}
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.1 + index * 0.1, duration: 0.4 }}
  >
    <span className="text-sm font-medium" style={{ color: secondaryColor || '#9ca3af' }}>{label}</span>
    <span className={`text-sm font-semibold text-right ${truncate ? 'truncate max-w-[60%]' : ''}`} style={{ color: accentColor || '#ffffff' }}>{value}</span>
  </motion.div>
);

type ModalMode = 'booking' | 'addon';

export const TicketModal: React.FC<TicketModalProps> = ({ bookingId, onClose }) => {
  const [bookingData, setBookingData] = useState<BookingResponseWithQRCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>('booking');
  const [selectedAddonIndex, setSelectedAddonIndex] = useState(0);
  const [isCreatingTransfer, setIsCreatingTransfer] = useState(false);
  
  // Get test status and class from context
  const { getTestStatus } = useStatusTest();
  const { globalTestClass } = useClassTest();
  
  const navigate = useNavigate();
  // Get business handle for currency formatting (must be called before early returns)
  // Use bookingData if available, otherwise null
  const event_booking_category = bookingData?.booking?.event_booking_category;
  const eventSchedule = event_booking_category?.event_schedule;
  const event = eventSchedule?.event;
  const formatPrice = (amount: number) => {
    const currency = bookingData?.booking?.currency;
    if (!currency) {
      console.warn('TicketModal: currency missing from booking data');
      return `${amount / 100}`;
    }
    return formatCurrencyForDisplay(amount, currency);
  };

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await ticketsService.getTicketById(bookingId);
        setBookingData(data);
      } catch (err) {
        console.error('Error fetching booking data:', err);
        setError('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingId]);

  if (loading) {
    return (
      <AnimatePresence>
        <motion.div
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            className="relative rounded-2xl w-full max-w-sm shadow-2xl h-[80vh] max-h-[80vh] flex flex-col overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(75, 85, 99, 0.3), rgba(31, 41, 55, 0.9))',
              borderWidth: '2px',
              borderStyle: 'solid',
              borderColor: 'rgba(107, 114, 128, 0.5)',
            }}
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              mass: 0.8
            }}
          >
            {/* Header Skeleton */}
            <div 
              className="p-6 flex flex-col items-center rounded-t-2xl flex-shrink-0"
              style={{
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(20px)',
              }}
            >
              {/* Event Title Skeleton */}
              <div className="h-6 w-48 bg-gray-700 rounded-lg animate-pulse mb-4" />
              
              {/* QR Code Skeleton */}
              <div className="p-1 rounded-xl" style={{ background: 'linear-gradient(135deg, #6b7280, #4b5563)' }}>
                <div className="w-48 h-48 rounded-lg bg-gray-800 animate-pulse flex items-center justify-center">
                  <div className="w-32 h-32 border-4 border-gray-600 rounded-lg animate-pulse" />
                </div>
              </div>
              
              {/* Instruction Text Skeleton */}
              <div className="h-3 w-40 bg-gray-700 rounded animate-pulse mt-3" />
            </div>

            {/* Tier Ribbon Skeleton */}
            <div className="h-12 bg-gray-800/50 flex items-center justify-center">
              <div className="h-5 w-32 bg-gray-700 rounded animate-pulse" />
            </div>

            {/* Details Section Skeleton */}
            <div 
              className="flex-1 overflow-hidden" 
              style={{ background: 'rgba(0, 0, 0, 0.3)' }}
            >
              <div className="p-6 space-y-4">
                {/* Detail Rows Skeleton */}
                {[1, 2, 3, 4, 5, 6].map((_, index) => (
                  <div 
                    key={index} 
                    className="flex justify-between items-center py-3 border-b border-gray-700/50"
                  >
                    <div 
                      className="h-4 bg-gray-700 rounded animate-pulse" 
                      style={{ width: `${60 + (index % 3) * 20}px`, animationDelay: `${index * 0.1}s` }}
                    />
                    <div 
                      className="h-4 bg-gray-600 rounded animate-pulse" 
                      style={{ width: `${80 + (index % 2) * 40}px`, animationDelay: `${index * 0.1 + 0.05}s` }}
                    />
                  </div>
                ))}
              </div>

              {/* Add-ons Section Skeleton */}
              <div className="px-6 pb-4">
                <div className="h-4 w-16 bg-gray-700 rounded animate-pulse mb-3" />
                <div className="space-y-2">
                  {[1, 2].map((_, index) => (
                    <div 
                      key={index}
                      className="rounded-lg p-3 flex items-center gap-3"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(107, 114, 128, 0.3)',
                      }}
                    >
                      <div className="w-12 h-12 rounded bg-gray-700 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-24 bg-gray-700 rounded animate-pulse" />
                        <div className="h-3 w-16 bg-gray-600 rounded animate-pulse" />
                      </div>
                      <div className="w-5 h-5 bg-gray-700 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer Buttons Skeleton */}
            <div 
              className="p-4 flex-shrink-0 space-y-3"
              style={{
                background: 'rgba(0, 0, 0, 0.5)',
                borderTop: '1px solid rgba(107, 114, 128, 0.3)',
              }}
            >
              <div className="h-12 w-full bg-gray-700 rounded-full animate-pulse" />
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (error || !bookingData) {
    return (
      <AnimatePresence>
        <motion.div
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm shadow-2xl p-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <div className="text-center text-red-400">{error || 'Failed to load booking'}</div>
            <motion.button
              onClick={onClose}
              className="mt-4 w-full px-6 py-3 bg-violet-600 text-white rounded-full font-medium hover:bg-violet-500"
            >
              Close
            </motion.button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  const { booking, qrcode, add_on_qrcodes } = bookingData;
  const { user, redeemed_at, add_ons, transfer_id, status: apiStatus, add_on_redeemed, early_entry_duration } = booking;
  
  // Use test status if set (dev mode), otherwise use API status
  const testStatus = getTestStatus(booking.id);
  const status = testStatus ?? apiStatus;
  
  // Handle abandoned status - show empty fallback
  if (isAbandonedStatus(status)) {
    return (
      <AnimatePresence>
        <motion.div
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm shadow-2xl p-8 text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <div className="text-gray-400 mb-4">This booking is no longer available.</div>
            <motion.button
              onClick={onClose}
              className="w-full px-6 py-3 bg-violet-600 text-white rounded-full font-medium hover:bg-violet-500"
            >
              Close
            </motion.button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }
  console.log("Debug Transfer:", { status, redeemed_at, transfer_id, user });
  const isQRUsed = redeemed_at !== null;

  // Check if booking is transferable:
  // redeemed_at is null && add_on_redeemed is false && status == 'Active'
  const isTransferable = 
    redeemed_at === null && 
    add_on_redeemed === false && 
    status === BookingStatus.ACTIVE;

  // Only allow transfer creation if transferable and no existing transfer
  const canCreateTransfer = isTransferable && !transfer_id;

  const isTransferred = !!transfer_id;
  const addons = add_ons || [];

  // Calculate Entry Time: EventSchedule.StartTime - EarlyEntryDuration (if not null, else 0)
  const scheduleStartTime = eventSchedule?.start_time ? new Date(eventSchedule.start_time) : new Date(booking.start_time);
  const earlyEntryMs = early_entry_duration ? early_entry_duration / 1_000_000 : 0; // Convert nanoseconds to milliseconds
  const entryTime = new Date(scheduleStartTime.getTime() - earlyEntryMs);
  const selectedAddon = addons[selectedAddonIndex];
  const selectedAddonQR = selectedAddon ? add_on_qrcodes[selectedAddon.add_on.id] : null;

  // Get tier styling from API data (or test override)
  const categoryClass = globalTestClass || booking.booking_category_class;
  const tierStyle = getTicketCategoryStyle(categoryClass);

  // Get event image for background
  const eventImageId = event?.card_image_id || event_booking_category?.booking_category?.card_image_id;
  const hasEventImage = eventImageId && eventImageId !== '00000000-0000-0000-0000-000000000000';
  const eventImageUrl = hasEventImage ? getSignedMediaUrl(eventImageId, 'image').url : null;

  const handleAddonClick = (index: number) => {
    setSelectedAddonIndex(index);
    setModalMode('addon');
  };

  const handleNextAddon = () => {
    if (selectedAddonIndex < addons.length - 1) {
      setSelectedAddonIndex(selectedAddonIndex + 1);
    }
  };

  const handlePrevAddon = () => {
    if (selectedAddonIndex > 0) {
      setSelectedAddonIndex(selectedAddonIndex - 1);
    }
  };

  const handleBackToBooking = () => {
    setModalMode('booking');
  };

  return (
    <AnimatePresence>
      <motion.div
        onClick={onClose}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          className="relative rounded-2xl w-full max-w-sm shadow-2xl h-[80vh] max-h-[80vh] flex flex-col overflow-hidden"
          style={{
            background: tierStyle.backgroundGradient !== 'none' ? tierStyle.backgroundGradient : '#111827',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: tierStyle.borderColor,
            boxShadow: `0 25px 50px -12px ${tierStyle.glowColor}`,
          }}
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            mass: 0.8
          }}
        >
          {modalMode === 'booking' ? (
            <>
              {/* Header with Event Background and Status Overlay */}
              <motion.div
                className="flex flex-col items-center rounded-t-2xl flex-shrink-0 relative overflow-hidden"
                style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  // Fixed height for consistent layout
                  height: status !== BookingStatus.ACTIVE ? '310px' : 'auto',
                  padding: status !== BookingStatus.ACTIVE ? '1.5rem' : '1.5rem',
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
              >
                {/* Event Background Image */}
                {eventImageUrl && (
                  <div className="absolute inset-0 opacity-30">
                    <img
                      src={eventImageUrl}
                      alt="Event"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Status Overlay - Spans entire header for non-active status */}
                {status !== BookingStatus.ACTIVE && (
                  <StatusOverlay status={status} variant="modal" />
                )}
                
                {/* Event Title - Always at top */}
                <motion.h3
                  className="relative z-20 text-xl font-serif font-bold text-center text-white"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  {event?.name || 'Event'}
                </motion.h3>
                
                {/* QR Code - Only show when status is ACTIVE */}
                {status === BookingStatus.ACTIVE && (
                  <div 
                    className="relative z-10 p-1 rounded-xl mt-4"
                    style={{
                      background: `linear-gradient(135deg, ${tierStyle.borderColor}, ${tierStyle.hoverBorderColor})`,
                    }}
                  >
                    <div className="relative">
                      {isQRUsed ? (
                        <motion.div
                          className="w-48 h-48 rounded-lg flex items-center justify-center bg-gray-900"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3, duration: 0.5 }}
                        >
                          <div className="text-center text-red-400 px-4">
                            <p className="font-bold text-lg mb-2">QR Code Used</p>
                            <p className="text-sm">This booking QR has been redeemed</p>
                          </div>
                        </motion.div>
                      ) : qrcode ? (
                        <motion.img
                          src={`data:image/png;base64,${qrcode}`}
                          alt="Ticket QR Code"
                          className="w-48 h-48 rounded-lg bg-white"
                          width="192"
                          height="192"
                          initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                          animate={{ opacity: 1, scale: 1, rotate: 0 }}
                          transition={{
                            delay: 0.3,
                            duration: 0.5,
                            type: "spring",
                            stiffness: 300,
                            damping: 20
                          }}
                          whileHover={{
                            scale: 1.05,
                            rotate: 2,
                            transition: { type: "spring", stiffness: 400, damping: 10 }
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                )}
                
                {/* Spacer to push helper text to bottom when showing status overlay */}
                {status !== BookingStatus.ACTIVE && <div className="flex-1" />}
                
                {/* Helper Text - At bottom */}
                <motion.p
                  className="relative z-20 text-xs text-center px-4 w-full text-white font-bold mt-auto"
                  style={{
                    textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  {getStatusMessage(status)}
                </motion.p>
              </motion.div>

              {/* Tier Ribbon Separator */}
              <TierRibbon
                tierClass={categoryClass}
                variant="ticket-center"
                textSuffix="TICKET"
              />

              {/* Scrollable Content Section */}
              <div 
                className="flex-1 overflow-y-auto scrollbar-hide" 
                style={{ 
                  background: 'rgba(0, 0, 0, 0.3)',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {/* Details Section */}
                <motion.div
                  className="p-6 space-y-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  {user && (
                    <DetailRow label="Booked For" value={`${user.first_name} ${user.last_name}`} index={0} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                  )}
                  {event_booking_category?.booking_category && (
                    <DetailRow label="Ticket Type" value={event_booking_category.booking_category.name} index={1} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                  )}
                  <DetailRow label="Date" value={entryTime.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} index={2} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                  <DetailRow label="Entry Time" value={entryTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })} index={3} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                  {event_booking_category?.price && (
                    <DetailRow label="Price" value={formatPrice(event_booking_category.price)} index={4} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                  )}
                  <DetailRow label="Booking ID" value={booking.id.split('-')[0]} index={5} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                </motion.div>

                {/* Addon Cards Section */}
                {addons.length > 0 && (
                  <motion.div
                    className="px-6 pb-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <h4 className="text-sm font-semibold text-white mb-3">Add-ons</h4>
                    <div className="space-y-2">
                      {addons.map((addon, index) => {
                        const addonImageUrl = getSignedMediaUrl(addon.add_on.image_id, 'image').url;
                        const addonQR = add_on_qrcodes[addon.add_on.id];
                        const isAddonQRUsed = addon.remaining_quantity === 0;

                        return (
                          <motion.button
                            key={addon.add_on.id || `addon-${index}`}
                            onClick={() => handleAddonClick(index)}
                            className="w-full rounded-lg p-3 flex items-center gap-3 transition-colors"
                            style={{
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: `1px solid ${tierStyle.borderColor}50`,
                            }}
                            whileHover={{ 
                              scale: 1.02,
                              borderColor: tierStyle.borderColor,
                            }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <img
                              src={addonImageUrl}
                              alt={addon.add_on.name}
                              className="w-12 h-12 rounded object-cover"
                            />
                            <div className="flex-1 text-left">
                              <p className="text-sm font-medium text-white">{addon.add_on.name}</p>
                              <p className="text-xs text-gray-400">Qty: {addon.quantity}</p>
                            </div>
                            {isAddonQRUsed ? (
                              <span className="text-xs text-red-400">Used</span>
                            ) : addonQR ? (
                              <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                            ) : null}
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Transfer and Close Buttons */}
              <motion.div
                className="p-4 flex-shrink-0 space-y-3"
                style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  borderTop: `1px solid ${tierStyle.borderColor}30`,
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                {/* Only show View Transfer button if transferable AND has existing transfer */}
                {isTransferable && isTransferred && transfer_id && (
                  <motion.button
                    onClick={() => {
                      onClose();
                      navigate(`/transfer-booking/t/${transfer_id}`);
                    }}
                    className="w-full px-6 py-3 bg-blue-600/20 border border-blue-500 text-blue-400 rounded-full font-medium hover:bg-blue-600 hover:text-white transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    View Transfer
                  </motion.button>
                )}

                {/* Only show Create Transfer button if transferable */}
                {canCreateTransfer && (
                  <motion.button
                    onClick={async () => {
                      try {
                        setIsCreatingTransfer(true);
                        const createdTransfer = await transferService.createTransfer(bookingId);
                        onClose();
                        navigate(`/transfer-booking/t/${createdTransfer.id}`);
                      } catch (err) {
                        console.error('Error creating transfer:', err);
                      } finally {
                        setIsCreatingTransfer(false);
                      }
                    }}
                    disabled={isCreatingTransfer}
                    className="w-full px-6 py-3 bg-transparent rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      border: `2px solid ${tierStyle.borderColor}`,
                      color: tierStyle.borderColor,
                    }}
                    whileHover={{ 
                      scale: isCreatingTransfer ? 1 : 1.02,
                      backgroundColor: `${tierStyle.borderColor}20`,
                    }}
                    whileTap={{ scale: isCreatingTransfer ? 1 : 0.95 }}
                  >
                    {isCreatingTransfer ? 'Creating...' : 'Create Transfer'}
                  </motion.button>
                )}
                <motion.button
                  onClick={onClose}
                  className="w-full px-6 py-3 rounded-full font-semibold"
                  style={{
                    background: tierStyle.buttonGradient,
                    color: tierStyle.buttonTextColor,
                  }}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: `0 10px 25px ${tierStyle.glowColor}`
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  Close
                </motion.button>
              </motion.div>
            </>
          ) : (
            <>
              {/* Addon QR View */}
              <motion.div
                className="p-6 flex flex-col items-center rounded-t-2xl flex-shrink-0 relative overflow-hidden"
                style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
              >
                {/* Addon Background Image */}
                {selectedAddon?.add_on.image_id && selectedAddon.add_on.image_id !== '00000000-0000-0000-0000-000000000000' && (
                  <div className="absolute inset-0 opacity-30">
                    <img
                      src={getSignedMediaUrl(selectedAddon.add_on.image_id, 'image').url}
                      alt="Add-on"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <motion.h3
                  className="relative z-10 text-xl font-serif font-bold text-white text-center mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  {selectedAddon?.add_on.name || 'Add-on'}
                </motion.h3>

                {/* Navigation Arrows */}
                <div className="relative z-10 flex items-center gap-4 w-full justify-center">
                  <motion.button
                    onClick={handlePrevAddon}
                    disabled={selectedAddonIndex === 0}
                    className={`p-2 rounded-full ${selectedAddonIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                    whileHover={selectedAddonIndex > 0 ? { scale: 1.1 } : {}}
                    whileTap={selectedAddonIndex > 0 ? { scale: 0.9 } : {}}
                  >
                    <ChevronLeftIcon className="w-6 h-6 text-white" />
                  </motion.button>

                  {/* QR Code with tier border */}
                  <div 
                    className="relative z-10 p-1 rounded-xl"
                    style={{
                      background: `linear-gradient(135deg, ${tierStyle.borderColor}, ${tierStyle.hoverBorderColor})`,
                    }}
                  >
                    {selectedAddonQR ? (
                      <motion.img
                        src={`data:image/png;base64,${selectedAddonQR}`}
                        alt="Add-on QR Code"
                        className="w-48 h-48 rounded-lg bg-white"
                        width="192"
                        height="192"
                        initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{
                          delay: 0.3,
                          duration: 0.5,
                          type: "spring",
                          stiffness: 300,
                          damping: 20
                        }}
                        whileHover={{
                          scale: 1.05,
                          rotate: 2,
                          transition: { type: "spring", stiffness: 400, damping: 10 }
                        }}
                      />
                    ) : (
                      <div className="w-48 h-48 rounded-lg flex items-center justify-center bg-gray-900">
                        <p className="text-gray-400 text-sm">No QR code available</p>
                      </div>
                    )}
                  </div>

                  <motion.button
                    onClick={handleNextAddon}
                    disabled={selectedAddonIndex === addons.length - 1}
                    className={`p-2 rounded-full ${selectedAddonIndex === addons.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                    whileHover={selectedAddonIndex < addons.length - 1 ? { scale: 1.1 } : {}}
                    whileTap={selectedAddonIndex < addons.length - 1 ? { scale: 0.9 } : {}}
                  >
                    <ChevronRightIcon className="w-6 h-6 text-white" />
                  </motion.button>
                </div>

                <motion.p
                  className="relative z-10 text-xs text-gray-400 mt-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  {selectedAddonIndex + 1} of {addons.length}
                </motion.p>
              </motion.div>

              {/* Scrollable Content Section */}
              <div 
                className="flex-1 overflow-y-auto scrollbar-hide" 
                style={{ 
                  background: 'rgba(0, 0, 0, 0.3)',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {/* Addon Details Section */}
                {selectedAddon && (
                  <motion.div
                    className="p-6 space-y-0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                  >
                    <DetailRow label="Name" value={selectedAddon.add_on.name} index={0} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                    <DetailRow
                      label="Description"
                      value={selectedAddon.add_on.description || 'N/A'}
                      index={1}
                      truncate={true}
                      accentColor={tierStyle.textColor}
                      secondaryColor={tierStyle.secondaryTextColor}
                      borderColor={`${tierStyle.borderColor}30`}
                    />
                    <DetailRow label="Price" value={formatPrice(selectedAddon.price)} index={2} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                    <DetailRow label="Quantity" value={selectedAddon.quantity} index={3} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                    <DetailRow label="Remaining" value={selectedAddon.remaining_quantity} index={4} accentColor={tierStyle.textColor} secondaryColor={tierStyle.secondaryTextColor} borderColor={`${tierStyle.borderColor}30`} />
                  </motion.div>
                )}
              </div>

              {/* Back and Close Buttons */}
              <motion.div
                className="p-4 space-y-3 flex-shrink-0"
                style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  borderTop: `1px solid ${tierStyle.borderColor}30`,
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <motion.button
                  onClick={handleBackToBooking}
                  className="w-full px-6 py-3 bg-transparent rounded-full font-medium"
                  style={{
                    border: `2px solid ${tierStyle.borderColor}`,
                    color: tierStyle.borderColor,
                  }}
                  whileHover={{ 
                    scale: 1.02,
                    backgroundColor: `${tierStyle.borderColor}20`,
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  Back to Booking
                </motion.button>
                <motion.button
                  onClick={onClose}
                  className="w-full px-6 py-3 rounded-full font-semibold"
                  style={{
                    background: tierStyle.buttonGradient,
                    color: tierStyle.buttonTextColor,
                  }}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: `0 10px 25px ${tierStyle.glowColor}`
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  Close
                </motion.button>
              </motion.div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
